import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import pomeranianMix from "@/assets/pomeranian-mix-after-grooming-denver.jpg";
import afterTerrier from "@/assets/terrier-after-grooming-denver.jpg";
import afterHavanese from "@/assets/havanese-designer-cut-denver.jpg";
import afterSchnauzer from "@/assets/miniature-schnauzer-after-grooming-highlands-ranch-denver.jpg";
import afterLabradoodle from "@/assets/labradoodle-after-grooming-cherry-creek-denver-october-2025.jpg";
import afterShihTzu from "@/assets/shih-tzu-after-grooming-denver.jpg";
interface HappyPet {
  image: string;
  name: string;
  breed: string;
  location: string;
  alt: string;
  size: 'small' | 'medium' | 'large';
}
interface AdditionalTransformation {
  after: string;
  afterAlt: string;
  petType: string;
  description: string;
}
interface HappyPetsGalleryProps {
  additionalTransformations?: AdditionalTransformation[];
}
const happyPets: HappyPet[] = [{
  image: pomeranianMix,
  name: "Milo",
  breed: "Pomeranian Mix",
  location: "Capitol Hill",
  alt: "Happy Pomeranian Mix after designer cut grooming in Capitol Hill Denver",
  size: 'large'
}, {
  image: afterLabradoodle,
  name: "Stella",
  breed: "Labradoodle",
  location: "Cherry Creek",
  alt: "Happy Labradoodle after grooming transformation in Cherry Creek Denver",
  size: 'large'
}, {
  image: afterSchnauzer,
  name: "Oscar",
  breed: "Miniature Schnauzer",
  location: "Highlands Ranch",
  alt: "Happy Miniature Schnauzer after puppy cut in Highlands Ranch Denver",
  size: 'small'
}, {
  image: afterHavanese,
  name: "Charlie",
  breed: "Havanese",
  location: "Wash Park",
  alt: "Happy Havanese after designer grooming cut in Washington Park Denver",
  size: 'small'
}, {
  image: afterTerrier,
  name: "Rusty",
  breed: "Terrier Mix",
  location: "DTC",
  alt: "Happy Terrier after professional grooming service in Denver Tech Center",
  size: 'medium'
}, {
  image: afterShihTzu,
  name: "Buddy",
  breed: "Shih Tzu Mix",
  location: "Highlands",
  alt: "Happy Shih Tzu after professional grooming service in Highlands Denver",
  size: 'small'
}];
const HappyPetsGallery = ({
  additionalTransformations = []
}: HappyPetsGalleryProps) => {
  const [hoveredPet, setHoveredPet] = useState<number | null>(null);

  // Combine additional transformations with existing happy pets
  const allPets: HappyPet[] = [...additionalTransformations.map((transformation, index) => ({
    image: transformation.after,
    name: transformation.petType.split(' ')[0] || `Pet ${index + 1}`,
    breed: transformation.petType,
    location: transformation.description,
    alt: transformation.afterAlt,
    size: (index % 3 === 0 ? 'large' : index % 2 === 0 ? 'medium' : 'small') as 'small' | 'medium' | 'large'
  })), ...happyPets];
  return <section className="py-20 bg-gradient-to-b from-accent/5 to-transparent">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12 space-y-4">
          <h2 className="text-3xl md:text-5xl font-bold">
            Happy Pets, Happier Humans ❤️
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Meet some of our wonderful clients who love their fresh new looks! 
            Every tail wag and purr makes our day.
          </p>
        </div>

        {/* Masonry Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {allPets.map((pet, index) => <div key={index} className={`relative group cursor-pointer overflow-hidden rounded-2xl shadow-soft hover:shadow-luxury transition-all duration-300 ${pet.size === 'large' ? 'row-span-2' : pet.size === 'medium' ? 'row-span-1' : 'row-span-1'}`} onMouseEnter={() => setHoveredPet(index)} onMouseLeave={() => setHoveredPet(null)}>
              {/* Pet Image */}
              <img src={pet.image} alt={pet.alt} className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-500" loading="lazy" />
              
              {/* Hover Overlay */}
              <div className={`absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent 
                  flex flex-col justify-end p-4 transition-opacity duration-300
                  ${hoveredPet === index ? 'opacity-100' : 'opacity-0'}`}>
                <h3 className="text-white font-bold text-lg">{pet.name}</h3>
                <p className="text-white/90 text-sm">{pet.breed}</p>
                <p className="text-white/70 text-xs">{pet.location}</p>
              </div>
            </div>)}
        </div>

        {/* Optional: Add fun stats or CTA */}
        <div className="text-center mt-12 space-y-6">
          <p className="text-muted-foreground text-lg">
            Join over <span className="font-bold text-primary">500+ happy pets</span> across Denver who love Groomie Roomie! 🐾
          </p>
          <Button asChild variant="hero" size="lg">
            <Link to="/gallery">View Full Gallery</Link>
          </Button>
        </div>
      </div>
    </section>;
};
export default HappyPetsGallery;